/**
 * The roll register (sprint decision Q2, ADR 0035): one session's roll as a
 * register on a people template. One row per enrolled learner, the name as
 * text, the marks in place as one keyboard group (each read as its word; the
 * letters P A E L are taught once in the hint), the camera mark where
 * eligible, and "Not recorded" as plain meta where there is no mark. The head
 * is "Roll" with the count recorded and one quiet act, Mark all present.
 *
 * ONE COMPONENT, TWO HOMES. The session portal mounts it as the roll on the
 * session, where Submit roll is the time tile's server-ranked act and reaches
 * this register through `controlsRef`. The People view mounts the same
 * register for correcting any session later, and carries Submit roll in the
 * register's own foot because that page has no time tile.
 *
 * THE GUARANTEES ARE THE SAME IN BOTH. A draft per session survives a reload;
 * submit is one act; a save that meets a newer roll shows the conflict with
 * their marks and never silently overwrites; a learner with no mark reads
 * "Not recorded", never absent; letters mark and move, arrows move within a
 * row. `markAttendance` stays the single source of truth for a confirmed
 * mark.
 */
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type ComponentProps, type KeyboardEvent, type Ref, type RefObject } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Video, VideoOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ConflictNotice, type ConflictFact } from "@/components/ui/conflict-notice";
import { Kbd } from "@/components/ui/kbd";
import { Register, RegisterHead, RegisterRow, RegisterTitle } from "@/components/ui/register";
import { useAuth } from "@/contexts/AuthContext";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useRovingFocus } from "@/hooks/use-roving-focus";
import { attendanceApi, type AttendanceStatus, type SessionAttendance } from "@/lib/api/attendance";
import { ApiError } from "@/lib/api/client";
import { currentOnConflict } from "@/lib/api/conflict";
import { registerText, registerPhrases } from "@/lib/register-copy";
import { cn } from "@/lib/utils";
import { rollRecordingState } from "@/components/session/session-close-plan";
import { sessionRollSummary } from "@/components/session/session-roll-summary";

import { ROLL_RECOVERY_CLEARED, useRollDraft, readRollExpanded, writeRollExpanded, type RollMark, type RollDraft } from "./roll-recovery";
import { sessionRollQueryKey } from "./use-session-roll";

const STATUS_FULL_LABEL: Record<AttendanceStatus, string> = registerPhrases.attendance;
const STATUSES: AttendanceStatus[] = ["present", "absent", "excused", "late"];
/** The marks' letter keys: the same letters the marks read. */
const STATUS_KEY: Record<string, AttendanceStatus> = { p: "present", a: "absent", e: "excused", l: "late" };
const CAMERA_ELIGIBLE = new Set<AttendanceStatus>(["present", "late"]);

type LocalMark = RollMark;

/**
 * One learner's marks as one radio group with a roving tab stop: Tab reaches
 * the row once, the arrows move and select within it, and a letter key marks
 * the row and hands focus to the next learner's group. Each mark reads as its
 * word; the letters are taught once, in the register's hint, never per row.
 */
export function AttendanceStatusGroup({
  groupRef,
  name,
  status,
  disabled,
  onSelect,
  onAdvance,
}: {
  groupRef: (node: HTMLDivElement | null) => void;
  name: string;
  status: AttendanceStatus | undefined;
  disabled: boolean;
  onSelect: (status: AttendanceStatus) => void;
  /** Focus the next learner's group; false when this is the last row. */
  onAdvance: () => boolean;
}) {
  const radios = useRovingFocus<HTMLButtonElement>({
    count: STATUSES.length,
    orientation: "both",
    loop: true,
    activeIndex: status ? STATUSES.indexOf(status) : undefined,
    onMove: (index) => onSelect(STATUSES[index]),
  });

  const markByLetter = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled || event.altKey || event.ctrlKey || event.metaKey) return;
    const next = STATUS_KEY[event.key.toLowerCase()];
    if (!next) return;
    event.preventDefault();
    onSelect(next);
    if (!onAdvance()) radios.focus(STATUSES.indexOf(next));
  };

  return (
    <div ref={groupRef} role="radiogroup" aria-label={registerPhrases.attendanceFor(name)} className="flex flex-wrap gap-1" onKeyDown={markByLetter}>
      {STATUSES.map((s, index) => (
        <Button
          key={s}
          {...radios.itemProps(index)}
          type="button"
          role="radio"
          size="sm"
          className="min-w-[var(--control-height-compact)] max-sm:px-2"
          variant={status === s ? "secondary" : "outline"}
          aria-checked={status === s}
          onClick={() => onSelect(s)}
          disabled={disabled}
          aria-label={STATUS_FULL_LABEL[s]}
        >
          {STATUS_FULL_LABEL[s]}
        </Button>
      ))}
    </div>
  );
}

/**
 * The letter keys, taught once for the whole register rather than on every
 * row. Only a fine pointer is shown it: that is where a keyboard is at hand.
 */
export function MarkKeysHint({ className }: { className?: string }) {
  return (
    <p className={cn("hidden flex-wrap items-center gap-x-1.5 gap-y-1 pointer-fine:flex", className)}>
      <Kbd>{registerText.p}</Kbd><Kbd>{registerText.a}</Kbd><Kbd>{registerText.e}</Kbd><Kbd>{registerText.l}</Kbd> {registerText.markALearnerAndMoveToTheNext}</p>
  );
}

/**
 * The camera mark, where a present or late learner's camera is recorded: one
 * checkbox on the same 30 px footing as the marks, so every control in
 * a row meets the pointer floor the marks meet.
 */
function CameraMark({ name, on, disabled, onChange }: {
  name: string;
  on: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <Button
      type="button"
      role="checkbox"
      size="icon-sm"
      variant={on ? "secondary" : "outline"}
      aria-checked={on}
      aria-label={registerPhrases.cameraFor(name)}
      disabled={disabled}
      onClick={() => onChange(!on)}
    >
      {on ? <Video aria-hidden /> : <VideoOff aria-hidden />}
    </Button>
  );
}

/** The marks a roll holds, as the register edits them. */
function marksOf(roster: SessionAttendance["roster"]): Record<string, LocalMark> {
  const marks: Record<string, LocalMark> = {};
  for (const r of roster) {
    if (r.status) marks[r.student_id] = { status: r.status, camera_on: r.camera_on ?? false };
  }
  return marks;
}

/** How many learners' marks here differ from the saved roll: what Submit roll would send. */
function countUnsent(marks: Record<string, LocalMark>, roster: SessionAttendance["roster"]): number {
  return roster.filter((r) => {
    const own = marks[r.student_id];
    if (!own) return false;
    if (own.status !== r.status) return true;
    return CAMERA_ELIGIBLE.has(own.status) && own.camera_on !== (r.camera_on ?? false);
  }).length;
}

function changedMarks(marks: Record<string, LocalMark>, base: Record<string, LocalMark>): Record<string, LocalMark> {
  return Object.fromEntries(Object.entries(marks).filter(([id, mark]) => mark.status !== base[id]?.status
    || CAMERA_ELIGIBLE.has(mark.status) && mark.camera_on !== base[id]?.camera_on));
}

/**
 * How long a burst of marks is left to settle before the unsent count is
 * said. Letters mark a roster one learner a keystroke; the sentence is
 * announced once the burst stops, not on every key.
 */
export const UNSENT_SETTLE_MS = 800;

/** Each learner whose saved mark differs from the one here, as their mark reads. */
function rollConflictFacts(
  theirs: SessionAttendance,
  mine: (studentId: string) => LocalMark | undefined,
): ConflictFact[] {
  return theirs.roster.flatMap((r) => {
    const own = mine(r.student_id);
    const saved: LocalMark | undefined = r.status ? { status: r.status, camera_on: r.camera_on ?? false } : undefined;
    if (!saved && !own) return [];
    const sameStatus = saved?.status === own?.status;
    const sameCamera = !saved || !CAMERA_ELIGIBLE.has(saved.status) || saved.camera_on === own?.camera_on;
    if (sameStatus && sameCamera) return [];
    const reads = !saved
      ? registerText.notMarked
      : sameStatus
        ? registerPhrases.savedMark(STATUS_FULL_LABEL[saved.status], saved.camera_on)
        : STATUS_FULL_LABEL[saved.status];
    return [{ label: registerPhrases.markFor(r.preferred_name ?? r.legal_name), theirs: reads }];
  });
}

function offRosterIds(reason: string): string[] | null {
  const prefix = registerText.theseStudentIdSAreNotOnThisCourseS;
  return reason.trim().startsWith(prefix) ? reason.trim().slice(prefix.length).split(",").map(id => id.trim()) : null;
}

/** Known route refusals name the teacher's roll and learners, never request fields or ids. */
function rollRefusalWords(reason: string, roster: SessionAttendance["roster"]): string {
  const words = reason.trim();
  const ids = offRosterIds(words);
  if (ids) {
    const names = ids.map(id => roster.find(row => row.student_id === id)).map(row => row ? row.preferred_name ?? row.legal_name : null);
    // One wording for any count: a known name list, else the learners unnamed.
    if (!names.every((name): name is string => Boolean(name))) return registerText.marksForLearnersNoLongerOnThisCourseSRoster;
    const list = registerPhrases.nameList(names);
    return registerPhrases.removedMarks(list);
  }
  if (words === registerText.thisSessionIsCancelledAttendanceCannotBeMarked) return registerText.thisSessionIsCancelledSoItsRollCanTBe2;
  if (words === registerText.provideEitherMarkAllPresentTrueOrANonEmpty) return registerText.markAtLeastOneLearnerBeforeRecordingTheRoll;
  if (words === registerText.noStudentsToMarkEmptyRoster) return registerText.noLearnersAreEnrolledOnThisCourseThereIsNo;
  if (words.startsWith(registerText.markAllPresentAndMarksAreMutuallyExclusive)) return registerText.reloadTheRollBeforeTryingAgain;
  return words;
}

function matchesRecordedMarks(saved: SessionAttendance, marks: Record<string, LocalMark>): boolean {
  return saved.status === "held"
    && Object.keys(marks).every(id => saved.roster.some(row => row.student_id === id))
    && rollConflictFacts(saved, id => marks[id]).length === 0;
}

type RollConflict = { theirs: SessionAttendance; bulk: boolean };
const LEGACY_DRAFT_REASON = registerText.thisOlderDraftHasNoStartingRollToCompareLoad;

/**
 * A save's conflict notice, given focus as it appears: the save stays disabled
 * until the user chooses, so focus moves to the choice rather than the page.
 */
function FocusedConflictNotice({ focusRef: ref, ...props }: ComponentProps<typeof ConflictNotice> & { focusRef: RefObject<HTMLDivElement | null> }) {
  useEffect(() => ref.current?.focus(), [ref]);
  return (
    <div ref={ref} tabIndex={-1} className="outline-none [&_p:empty]:hidden">
      <ConflictNotice {...props} />
    </div>
  );
}

/** What the register's host can do to it: the time tile's acts reach the roll through these. */
export type RollSubmitOutcome = { kind: "recorded" | "adopted"; roll: SessionAttendance } | { kind: "conflict"; theirs: SessionAttendance } | { kind: "refused"; reason: string } | { kind: "unconfirmed" };

export interface RollRegisterControls {
  /** Move focus to the first learner's marks, or to the register when it has no rows. */
  focus: () => void;
  /** Restore focus to the chooser after its host reveals a folded task. */
  focusConflict: () => void;
  /** Submit the roll as it stands: the same act as the register's own Submit roll. */
  submit: () => Promise<SessionAttendance | null>;
  submitOutcome: (requestId?: string) => Promise<RollSubmitOutcome>;
  resolveConflict: (choice: "mine" | "theirs", requestId?: string) => Promise<RollSubmitOutcome>;
}

/** Whether the roll can be submitted now, for a host that carries the act. */
export interface RollSubmitState {
  canSubmit: boolean;
  submitting: boolean;
  markedCount?: number;
  unmarkedCount?: number;
  changedCount?: number;
  changedMarks?: { id: string; sentence: string }[];
  held?: boolean;
  summary?: string;
}

/** The session's state, in one quiet line. */
const CLASS_STATE_LINE: Record<SessionAttendance["status"], string> = {
  scheduled: registerText.thisSessionIsScheduledSubmittingTheRollMarksItHeld,
  held: registerText.changesUpdateItsRecordedRoll,
  cancelled: registerText.thisSessionIsCancelledSoItsRollCanTBe,
};
/** A scheduled session whose time has passed with nothing recorded, said where the roll is taken. */
const ENDED_LINE = registerText.thisSessionHasEndedSubmittingTheRollMarksItHeld;

export function RollRegister({
  roll,
  ended = false,
  participation = true,
  onSubmitted,
  onConflictChoice,
  onConflictDetected,
  conflictMessage,
  conflictLabels,
  controlsRef,
  onSubmitStateChange,
  foot = "none",
  headingLevel,
  showHeading = true,
  recoveryUntil: _recoveryUntil = "session-close",
  className,
  rowsOnly = false,
  readOnly = false,
  foldSettled = rowsOnly,
}: {
  roll: SessionAttendance;
  /**
   * The server says the session's time has passed (clock phase
   * `status_pending`). The session portal knows it and says it here, once; the
   * People view reads no clock and says the session is scheduled.
   */
  ended?: boolean;
  /**
   * Whether a row says how the learner takes the course (Audit). The session
   * portal's register has no roster above it and says it; People's roster
   * already says it on the same learner's row, so its roll strip does not.
   */
  participation?: boolean;
  onSubmitted?: (saved: SessionAttendance) => void;
  /** A host owning the full act also owns the conflict continuation. */
  onConflictChoice?: (choice: "mine" | "theirs") => void;
  /** A reread can discover the conflict before a submit. The host keeps its intent. */
  onConflictDetected?: (theirs: SessionAttendance) => void;
  /** The host's lead sentence stays with the focused conflict and its choices. */
  conflictMessage?: (theirs: SessionAttendance) => string;
  conflictLabels?: { keepMine: string | null; loadTheirs: string };
  controlsRef?: Ref<RollRegisterControls>;
  /** Told whenever the roll's submit availability changes, for a host that carries the act. */
  onSubmitStateChange?: (state: RollSubmitState) => void;
  /**
   * Whether the register's foot carries Submit roll: `filled` on People, which
   * has no time tile; `quiet` on the portal when the server allowed the act
   * but ranked another; `none` when the time tile carries it.
   */
  foot?: "filled" | "quiet" | "none";
  headingLevel?: "h2" | "h3";
  showHeading?: boolean;
  recoveryUntil?: "session-close" | "roll-recorded";
  className?: string;
  rowsOnly?: boolean;
  readOnly?: boolean;
  foldSettled?: boolean;
}) {
  const sessionId = roll.session_id;
  const queryClient = useQueryClient();
  const tileRef = useRef<HTMLElement>(null);
  // The newest roll known here: the read, or a save's answer before the read catches up.
  const [latest, setLatest] = useState<SessionAttendance>(roll);
  const [inlineResult, setInlineResult] = useState<string | null>(null);
  const resultFocus = useRef<HTMLParagraphElement>(null);
  useEffect(() => { if (inlineResult && !rowsOnly) resultFocus.current?.focus(); }, [inlineResult, rowsOnly]);
  const [marks, setMarks] = useState<Record<string, LocalMark>>({});
  const currentMarks = useRef(marks);
  currentMarks.current = marks;
  const baseMarks = useRef(marksOf(roll.roster));
  const [needsDraftBase, setNeedsDraftBase] = useState(false);
  const [legacyDraft, setLegacyDraft] = useState<Record<string, LocalMark> | null>(null);
  const localDraft = useRef(false);
  // The roll's version these marks were made against, sent with every submit.
  // A ref, so the draft written inside a marks updater carries the current one.
  const rollVersion = useRef<string | null>(roll.roll_updated_at);
  // A newer roll met a submit: nothing is sent until the user chooses.
  const [conflict, setConflict] = useState<RollConflict | null>(null);
  const conflictDetected = useRef(onConflictDetected); conflictDetected.current = onConflictDetected;
  const lastRead = useRef(roll);
  const conflictFocus = useRef<HTMLDivElement>(null);
  // The draft is this teacher's own (ADR 0032): bound to the tenant and login
  // that made it, and offered back to no other.
  const { me } = useAuth();
  const tenant = me?.tenant_id ?? "";
  const owner = me?.user_id ?? "";
  const keptRoll = useRollDraft(sessionId, roll.roll_updated_at);
  const storedDraft = keptRoll.value;
  const endDraft = keptRoll.end;
  const setStoredDraft = keptRoll.set;
  const writeDraft = useCallback((draft: RollDraft) => {
    if (Object.keys(draft.marks).length || Object.keys(draft.reentry_marks ?? {}).length) setStoredDraft(draft);
    else endDraft();
  }, [setStoredDraft, endDraft]);
  const [folded, setFolded] = useState(() => foldSettled && roll.status === "held" && !storedDraft && readRollExpanded(tenant, owner, sessionId) !== true);
  const foldButton = useRef<HTMLButtonElement>(null);
  // Log out here or in another tab removes every draft; this register then
  // stops writing one, so nothing it holds outlives the sign-out.
  const keepsDraft = useRef(true);
  useEffect(() => {
    const stop = () => { keepsDraft.current = false; };
    window.addEventListener(ROLL_RECOVERY_CLEARED, stop);
    return () => window.removeEventListener(ROLL_RECOVERY_CLEARED, stop);
  }, []);
  const keepDraft = (next: Record<string, LocalMark>, reentry = legacyDraft) => {
    if (keepsDraft.current) writeDraft({ marks: changedMarks(next, baseMarks.current), base_marks: baseMarks.current, ...(reentry ? { reentry_marks: reentry } : {}), roll_updated_at: rollVersion.current });
  };

  // Reseed from the session's existing marks whenever the CLASS or the login
  // changes (never on every background refetch, so an in-progress edit is
  // never clobbered). This teacher's recoverable draft takes priority over the
  // server seed: it represents marks they made and never confirmed, which is
  // exactly what a tab close, crash or lost session mid-roll must restore. A
  // confirmed submit (single or bulk) clears the draft below, so there is
  // never a stale one left to prefer once the server agrees with the roster.
  useEffect(() => {
    const draft = storedDraft;
    if (draft) {
      localDraft.current = Object.keys(draft.marks).length > 0;
      setLegacyDraft(draft.reentry_marks ?? null);
      setFolded(false);
      // Filtered to the CURRENT roster. A learner who withdrew after the draft
      // was written renders nowhere, but submit enumerates every key in
      // `marks`, and the API refuses an off-roster id. That would 422 the
      // whole roll over a mark the instructor cannot see, let alone remove.
      const enrolled = new Set(roll.roster.map((r) => r.student_id));
      rollVersion.current = draft.roll_updated_at;
      const missingBase = !draft.base_marks && draft.roll_updated_at !== roll.roll_updated_at;
      setNeedsDraftBase(missingBase);
      baseMarks.current = draft.base_marks ?? (draft.roll_updated_at === roll.roll_updated_at ? marksOf(roll.roster) : {});
      setMarks(Object.fromEntries(Object.entries({ ...baseMarks.current, ...draft.marks }).filter(([id]) => enrolled.has(id))));
      if (missingBase) { setLegacyDraft(draft.marks); setConflict({ theirs: roll, bulk: false }); setFolded(false); conflictDetected.current?.(roll); }
      return;
    }
    localDraft.current = false;
    setNeedsDraftBase(false);
    setLegacyDraft(null);
    rollVersion.current = roll.roll_updated_at;
    baseMarks.current = marksOf(roll.roster);
    setMarks(marksOf(roll.roster));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, tenant, owner]);

  useEffect(() => {
    if (lastRead.current === roll) return;
    lastRead.current = roll;
    // A delayed read must not undo a newer confirmed answer.
    if (rollVersion.current && (!roll.roll_updated_at || Date.parse(rollVersion.current) > Date.parse(roll.roll_updated_at))) return;
    setLatest(roll);
    // Withdrawal changes the roster without necessarily changing its roll version.
    // Remove invisible marks before another submit, keeping every enrolled edit.
    const enrolled = new Set(roll.roster.map(row => row.student_id));
    const currentMarks = Object.fromEntries(Object.entries(marks).filter(([id]) => enrolled.has(id)));
    if (Object.keys(currentMarks).length !== Object.keys(marks).length) {
      baseMarks.current = Object.fromEntries(Object.entries(baseMarks.current).filter(([id]) => enrolled.has(id)));
      localDraft.current = Object.keys(changedMarks(currentMarks, baseMarks.current)).length > 0;
      setMarks(currentMarks);
      if (keepsDraft.current) writeDraft({ marks: changedMarks(currentMarks, baseMarks.current), base_marks: baseMarks.current, ...(legacyDraft ? { reentry_marks: legacyDraft } : {}), roll_updated_at: rollVersion.current });
    }
    if (rollVersion.current === roll.roll_updated_at) return;
    const ownsDraft = localDraft.current || Object.keys(storedDraft?.marks ?? {}).length > 0;
    if (ownsDraft && (needsDraftBase || !matchesRecordedMarks(roll, currentMarks))) {
      setConflict(current => ({ theirs: roll, bulk: current?.bulk ?? false }));
      setFolded(false);
      conflictDetected.current?.(roll);
    } else {
      // No draft belongs to this teacher: the correction becomes the base,
      // including the version sent by their next edit.
      rollVersion.current = roll.roll_updated_at;
      baseMarks.current = marksOf(roll.roster);
      setMarks(marksOf(roll.roster));
      setConflict(null);
      localDraft.current = false;
      if (legacyDraft) writeDraft({ marks: {}, base_marks: baseMarks.current, reentry_marks: legacyDraft, roll_updated_at: rollVersion.current });
      else if (ownsDraft) endDraft();
      if (ownsDraft) onSubmitted?.(roll);
    }
  }, [roll, tenant, owner, sessionId, marks, needsDraftBase, legacyDraft, onSubmitted, storedDraft?.marks, writeDraft, endDraft]);

  // Persist at the action boundary. A React state updater can run during
  // render; publishing the shared kit draft there also updates its host.
  // The ref keeps consecutive marks current without a mount-time write effect.
  const changeMarks = (next: Record<string, LocalMark>, reentry = legacyDraft) => {
    currentMarks.current = next;
    localDraft.current = Object.keys(changedMarks(next, baseMarks.current)).length > 0;
    setMarks(next);
    keepDraft(next, reentry);
  };
  const setStatus = (studentId: string, status: AttendanceStatus) => {
    const m = currentMarks.current;
    changeMarks({ ...m, [studentId]: { status, camera_on: m[studentId]?.camera_on ?? false } });
  };
  const setCamera = (studentId: string, camera_on: boolean) => {
    const m = currentMarks.current;
    if (m[studentId]) changeMarks({ ...m, [studentId]: { ...m[studentId], camera_on } });
  };

  // A saved roll replaces the marks and their version, so the next submit is
  // made against it and never conflicts with itself. The marks now match the
  // server, so no draft is left to restore. The answer is the server's current
  // roll, so it is written into the read the hosts mount the register from:
  // the session's state (scheduled, held) follows the save at once, here and on
  // a return to the session within the read's stale time, with no second read.
  const adoptRoll = (saved: SessionAttendance, recordedHere = false) => {
    const containedFocus = tileRef.current?.contains(document.activeElement);
    rollVersion.current = saved.roll_updated_at;
    baseMarks.current = marksOf(saved.roster);
    setNeedsDraftBase(false);
    if (recordedHere) setLegacyDraft(null);
    localDraft.current = false;
    if (!recordedHere && legacyDraft) writeDraft({ marks: {}, base_marks: baseMarks.current, reentry_marks: legacyDraft, roll_updated_at: saved.roll_updated_at });
    else keptRoll.end();
    setMarks(marksOf(saved.roster));
    setLatest(saved);
    queryClient.setQueryData(sessionRollQueryKey(sessionId), saved);
    if (recordedHere && foldSettled) {
      setFolded(true);
      writeRollExpanded(tenant, owner, sessionId, false);
      if (containedFocus) requestAnimationFrame(() => foldButton.current?.focus());
    }
  };

  const matchesRecordedRoll = (saved: SessionAttendance) => !needsDraftBase && saved.session_id === sessionId && matchesRecordedMarks(saved, marks);

  // A newer roll is the conflict the notice resolves, never a toast.
  const refused = (bulk: boolean, fallback: string) => (err: Error) => {
    setFolded(false);
    const withdrawn = err instanceof ApiError ? offRosterIds(err.message) : null;
    if (withdrawn) {
      const enrolledMarks = (value: Record<string, LocalMark>) => Object.fromEntries(Object.entries(value).filter(([id]) => !withdrawn.includes(id)));
      baseMarks.current = enrolledMarks(baseMarks.current);
      const reentry = legacyDraft ? enrolledMarks(legacyDraft) : null;
      setLegacyDraft(reentry);
      changeMarks(enrolledMarks(currentMarks.current), reentry);
    }
    const theirs = currentOnConflict<SessionAttendance>(err);
    if (theirs && !bulk && matchesRecordedRoll(theirs)) {
      setConflict(null); adoptRoll(theirs, true); onSubmitted?.(theirs);
    } else if (theirs) setConflict({ theirs, bulk });
    else if (!onConflictChoice && !rowsOnly) setInlineResult(err instanceof ApiError ? rollRefusalWords(err.message, latest.roster) : fallback);
  };

  const bulkPresentMutation = useMutation({
    mutationFn: (expected: string | null) => attendanceApi.markAttendance(sessionId, { mark_all_present: true, expected_roll_updated_at: expected }),
    onSuccess: saved => {
      setInlineResult(registerText.allPresentMarksRecordedForThisSession);
      adoptRoll(saved, true);
      onSubmitted?.(saved);
    },
    onError: refused(true, registerText.couldNotMarkTheRoll),
  });
  const submitMutation = useMutation({
    mutationFn: ({ expected, closeUnchanged = false, requestId }: { expected: string | null; closeUnchanged?: boolean; requestId?: string }) => {
      const changes = changedMarks(marks, baseMarks.current);
      // Older scheduled sessions can already hold marks. Initial Close still
      // needs the attendance route to mark that session held; Update and Keep
      // always send only changes.
      const outgoing = closeUnchanged && Object.keys(changes).length === 0 ? marks : changes;
      const body = {
        marks: Object.entries(outgoing).map(([student_id, v]) => ({
          student_id,
          status: v.status,
          camera_on: CAMERA_ELIGIBLE.has(v.status) ? v.camera_on : undefined,
        })),
        expected_roll_updated_at: expected,
      };
      return requestId ? attendanceApi.markAttendance(sessionId, body, requestId) : attendanceApi.markAttendance(sessionId, body);
    },
    onSuccess: (saved) => {
      if (!rowsOnly) setInlineResult(registerText.rollRecordedForThisSession);
      // Same rule as the bulk path: only a server-confirmed write clears the
      // draft, never the click that starts the request.
      adoptRoll(saved, true);
      onSubmitted?.(saved);
    },
    onError: refused(false, registerText.couldNotSubmitTheRoll),
  });

  // Keeping my edits sends the same roll again, on their version.
  const keepMine = ({ theirs }: RollConflict) => {
    setConflict(null);
    rollVersion.current = theirs.roll_updated_at;
    if (!needsDraftBase) submitMutation.mutate({ expected: rollVersion.current });
  };
  const loadTheirs = ({ theirs }: RollConflict) => {
    // A background read may have outrun the conflict response. Never load
    // that older snapshot over a newer roll already known to this register.
    const saved = Date.parse(latest.roll_updated_at ?? "") > Date.parse(theirs.roll_updated_at ?? "") ? latest : theirs;
    setConflict(null);
    adoptRoll(saved);
    return saved;
  };

  const hasMarks = Object.keys(marks).length > 0;
  const submitting = submitMutation.isPending;
  const saving = submitting || bulkPresentMutation.isPending;
  const unsent = needsDraftBase ? 0 : countUnsent(changedMarks(marks, baseMarks.current), latest.roster);
  // The session's state is the newest roll's, never the read a save has outrun.
  const canMarkClass = latest.status !== "cancelled" && !readOnly && !needsDraftBase;
  const canSubmit = canMarkClass && hasMarks && !saving && conflict === null && (latest.status !== "held" || unsent > 0);
  // With every change reverted there is nothing of mine to keep: only their version is a choice.
  const keepsNothing = conflictLabels?.keepMine === null || (!needsDraftBase && unsent === 0);

  // Each row's radio group, so a letter key can hand focus to the next learner.
  const groups = useRef<(HTMLDivElement | null)[]>([]);
  const focusRow = (index: number): boolean => {
    const stop = groups.current[index]?.querySelector<HTMLElement>('[role="radio"][tabindex="0"]');
    stop?.focus();
    return Boolean(stop);
  };

  const outcomeOf = async (send: () => Promise<SessionAttendance>): Promise<RollSubmitOutcome> => {
    try { return { kind: "recorded", roll: await send() }; }
    catch (error) {
      const theirs = currentOnConflict<SessionAttendance>(error);
      if (theirs) return matchesRecordedRoll(theirs) ? { kind: "recorded", roll: theirs } : { kind: "conflict", theirs };
      if (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 408) return { kind: "refused", reason: rollRefusalWords(error.message, latest.roster) };
      return { kind: "unconfirmed" };
    }
  };
  const submitOutcome = (requestId?: string): Promise<RollSubmitOutcome> => canSubmit
    ? outcomeOf(() => submitMutation.mutateAsync({ expected: rollVersion.current, closeUnchanged: latest.status !== "held" && !conflict, requestId }))
    : Promise.resolve({ kind: "unconfirmed" });
  const submit = async () => { const result = await submitOutcome(); return result.kind === "recorded" ? result.roll : null; };
  const resolveConflict = async (choice: "mine" | "theirs", requestId?: string): Promise<RollSubmitOutcome> => {
    if (!conflict) return { kind: "unconfirmed" };
    if (choice === "theirs") return { kind: "adopted", roll: loadTheirs(conflict) };
    if (needsDraftBase) return { kind: "refused", reason: LEGACY_DRAFT_REASON };
    const { theirs } = conflict;
    setConflict(null); rollVersion.current = theirs.roll_updated_at;
    return outcomeOf(() => submitMutation.mutateAsync({ expected: rollVersion.current, requestId }));
  };
  useImperativeHandle(controlsRef, () => ({
    focus: () => {
      const focus = () => { if (!focusRow(0)) tileRef.current?.focus(); };
      if (folded) { setFolded(false); requestAnimationFrame(focus); } else focus();
    },
    focusConflict: () => conflictFocus.current?.focus(),
    submit, submitOutcome, resolveConflict,
  }));
  const draftRecording = latest.status === "held" ? registerText.changedNotRecorded : latest.roster.some(row => row.status) ? rollRecordingState(false, unsent > 0, latest.roster.filter(row => row.status).length, latest.roster.length) : registerText.notRecorded;
  const draftCounts = registerPhrases.rollCounts(Object.keys(marks).length, latest.roster.filter(row => !marks[row.student_id]).length, draftRecording);
  const rollSummary = `${rollRecordingState(latest.status === "held", unsent > 0, latest.roster.filter(row => row.status).length, latest.roster.length)} · ${sessionRollSummary(latest.roster, marks, unsent > 0 || latest.status !== "held" ? "unmarked" : "unrecorded")}`;
  const submitState = useMemo<RollSubmitState>(() => ({ canSubmit, submitting, markedCount: Object.keys(marks).length, unmarkedCount: latest.roster.filter(row => !marks[row.student_id]).length, changedCount: unsent, changedMarks: latest.roster.flatMap(row => {
    const mark = marks[row.student_id];
    if (!mark || (mark.status === row.status && (!CAMERA_ELIGIBLE.has(mark.status) || mark.camera_on === (row.camera_on ?? false)))) return [];
    return [{ id: row.student_id, sentence: registerPhrases.changedMark(row.preferred_name || row.legal_name, row.status ? STATUS_FULL_LABEL[row.status] : registerText.notMarked, STATUS_FULL_LABEL[mark.status], CAMERA_ELIGIBLE.has(mark.status) ? mark.camera_on : null) }];
  }), held: latest.status === "held", summary: rollSummary }), [canSubmit, submitting, marks, latest, unsent, rollSummary]);
  useEffect(() => {
    onSubmitStateChange?.(submitState);
  }, [onSubmitStateChange, submitState]);

  const roster = roll.roster;
  // The head counts the saved roll; the foot says what is not saved yet. The
  // sentence settles after a burst of marks and goes the moment the roll saves.
  const recorded = latest.roster.filter((r) => r.status).length;
  const settledUnsent = useDebouncedValue(unsent, UNSENT_SETTLE_MS);
  const unsentLine = rowsOnly || unsent === 0 || settledUnsent === 0
    ? ""
    : registerPhrases.unsentMarks(settledUnsent);
  const headingId = `roll-heading-${sessionId}`;

  const rollAction = !(folded && latest.status === "held") && canMarkClass && roster.length > 0 && !roster.every(row => marks[row.student_id]?.status === "present") && (
              <Button
                type="button"
                variant="subtle"
                size="sm"
                onClick={() => {
                  changeMarks(Object.fromEntries(roster.map(row => [row.student_id, { status: "present" as const, camera_on: currentMarks.current[row.student_id]?.camera_on ?? false }])));
                  if (!rowsOnly) { bulkPresentMutation.mutate(rollVersion.current); return; }
                  requestAnimationFrame(() => { if (!focusRow(0)) tileRef.current?.focus(); });
                }}
                disabled={saving || conflict !== null}
              >
                {registerText.markAllPresent}</Button>
            );

  return (
    <Register
      ref={tileRef}
      rowsOnly={rowsOnly}
      tabIndex={-1}
      template="minmax(0,1fr) auto"
      aria-labelledby={showHeading ? headingId : undefined}
      data-roll-register={sessionId}
      // The people template (no mark, no code): name, the state word, the marks.
      // On a phone the marks step under the name.
      className={cn("@container outline-none", className)}
      head={(
        <>
          {showHeading ? <RegisterHead
            id={headingId}
            as={headingLevel}
            title={registerText.roll}
            count={roster.length > 0 && !folded ? rowsOnly && (unsent > 0 || recorded === 0 && latest.status !== "held") ? draftCounts : registerPhrases.recordedCount(recorded, roster.length) : undefined}
            action={rollAction}
          /> : <div className="flex flex-wrap items-center gap-3 text-meta"><span>{draftCounts}</span>{rollAction}</div>}
          <div className="grid gap-1 px-5 pb-2.5 text-meta text-muted-foreground">
            {!rowsOnly && <p>{ended && latest.status === "scheduled" ? ENDED_LINE : CLASS_STATE_LINE[latest.status]}</p>}
            {legacyDraft && !needsDraftBase && <p>{registerText.earlierDraftToReEnter}{latest.roster.filter(row => legacyDraft[row.student_id]).map(row => {
              const mark = legacyDraft[row.student_id];
              return registerPhrases.namedMark(row.preferred_name ?? row.legal_name, STATUS_FULL_LABEL[mark.status], CAMERA_ELIGIBLE.has(mark.status) ? mark.camera_on : null);
            }).join("; ")}.</p>}
            {!(folded && latest.status === "held") && canMarkClass && roster.length > 0 && (
              <MarkKeysHint />
            )}
            {folded && <p data-roll-summary className="text-ui text-foreground-soft">{rollSummary}</p>}
            {foldSettled && roster.length > 0 && (latest.status === "held" || roster.every(row => marks[row.student_id])) && <Button ref={foldButton} type="button" variant="subtle" size="sm" className="justify-self-start" aria-expanded={!folded} onClick={() => { writeRollExpanded(tenant, owner, sessionId, folded); setFolded(value => !value); }}>{folded ? registerText.showRoll : registerText.hideRoll}</Button>}
          </div>
        </>
      )}
    >
      {roster.map((r, rowIndex) => {
        const name = r.preferred_name ?? r.legal_name;
        const local = marks[r.student_id];
        return (
          <RegisterRow key={r.student_id} hidden={folded} data-roll-row={r.student_id} className="@max-[42rem]:gap-y-2 @min-[42rem]:[--cols:minmax(0,1fr)_auto_auto]">
            <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 @max-[42rem]:col-start-1 @max-[42rem]:row-start-1">
              <RegisterTitle>{name}</RegisterTitle>
              {participation && r.participation_kind === "audit" && <span className="text-meta text-muted-foreground">{registerText.audit}</span>}
            </span>
            {/* A missing draft mark is distinct from a learner left unrecorded on a held roll. */}
            <span data-slot="roll-state" className="whitespace-nowrap text-meta text-muted-foreground @max-[42rem]:col-start-2 @max-[42rem]:row-start-1">
              {local ? null : latest.roster.find(row => row.student_id === r.student_id)?.status ? registerText.noMarkHere : latest.status === "held" ? registerText.notRecorded : registerText.noMark}
            </span>
            <span className="flex flex-wrap items-center gap-x-3 gap-y-2 @max-[42rem]:col-span-2 @max-[42rem]:row-start-2">
              <AttendanceStatusGroup
                groupRef={(node) => { groups.current[rowIndex] = node; }}
                name={name}
                status={local?.status}
                disabled={!canMarkClass || saving}
                onSelect={(status) => setStatus(r.student_id, status)}
                onAdvance={() => focusRow(rowIndex + 1)}
              />
              <CameraMark
                name={name}
                on={local?.camera_on ?? false}
                disabled={!canMarkClass || saving || !local || !CAMERA_ELIGIBLE.has(local.status)}
                onChange={(on) => setCamera(r.student_id, on)}
              />
            </span>
          </RegisterRow>
        );
      })}
      {roster.length === 0 && (
        <li className="border-t border-rule px-5 py-3 text-ui text-muted-foreground">{registerText.noLearnersAreEnrolled}</li>
      )}

      {conflict && (
        <li className="border-t border-rule px-5 py-3">
          <FocusedConflictNotice
            focusRef={conflictFocus}
            object={registerText.thisRoll}
            message={needsDraftBase ? LEGACY_DRAFT_REASON : `${conflictMessage?.(conflict.theirs) ?? registerText.someoneSavedANewerVersionOfThisRollYourMarks}${keepsNothing ? "" : registerText.keepingYourEditsRecordsYourChangedMarksAndKeepsTheirs}`}
            facts={rollConflictFacts(conflict.theirs, (studentId) => marks[studentId])}
            disabled={saving}
            keepMineLabel={needsDraftBase || keepsNothing ? null : conflictLabels?.keepMine}
            loadTheirsLabel={conflictLabels?.loadTheirs}
            onKeepMine={() => onConflictChoice ? onConflictChoice("mine") : keepMine(conflict)}
            onLoadTheirs={() => onConflictChoice ? onConflictChoice("theirs") : loadTheirs(conflict)}
          />
        </li>
      )}

      {/* The status line is always in the tree, so its first words are announced. */}
      <li className={cn(
        "flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-5",
        (foot !== "none" || unsentLine) && "border-t border-rule py-3",
      )}>
        <p role="status" aria-label={registerText.rollChanges} className="text-meta text-muted-foreground">{unsentLine}</p>
        {!rowsOnly && inlineResult && <p ref={resultFocus} tabIndex={-1} role="status">{inlineResult}</p>}
        {foot !== "none" && (
          <Button type="button" variant={foot === "filled" ? "default" : "outline"} onClick={() => void submit()} disabled={!canSubmit}>
            {submitting ? registerText.submitting : registerText.submitRoll}
          </Button>
        )}
      </li>
    </Register>
  );
}
